import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Key, Plus, Trash2, RefreshCw, Copy, Loader2, Eye, EyeOff, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

export default function APIKeys() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showNewKey, setShowNewKey] = useState(false);
  const [newKeyValue, setNewKeyValue] = useState('');
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(null);
  const [confirmRegen, setConfirmRegen] = useState(null);

  const fetchKeys = async () => {
    try {
      const r = await api.get('/tenant/api-keys');
      setKeys(r.data.api_keys || []);
    } catch { toast.error('Gagal memuat API keys'); }
    setLoading(false);
  };

  useEffect(() => { fetchKeys(); }, []);

  const handleCreate = async () => {
    if (!name.trim()) return toast.error('Nama key harus diisi');
    setSaving(true);
    try {
      const r = await api.post('/tenant/api-keys', { name: name.trim() });
      setNewKeyValue(r.data.key);
      setShowCreate(false);
      setShowNewKey(true);
      setName('');
      fetchKeys();
      toast.success('API key berhasil dibuat');
    } catch { toast.error('Gagal membuat API key'); }
    setSaving(false);
  };

  const handleRevoke = async (id) => {
    try {
      await api.delete(`/tenant/api-keys/${id}`);
      fetchKeys();
      toast.success('API key berhasil direvoke');
    } catch { toast.error('Gagal merevoke API key'); }
    setConfirmRevoke(null);
  };

  const handleRegenerate = async (id) => {
    try {
      const r = await api.post(`/tenant/api-keys/${id}/regenerate`);
      setNewKeyValue(r.data.key);
      setShowNewKey(true);
      fetchKeys();
      toast.success('API key berhasil digenerate ulang');
    } catch { toast.error('Gagal regenerate API key'); }
    setConfirmRegen(null);
  };

  const copyKey = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">API Keys</h1>
          <p className="text-muted-foreground">Kelola API key untuk mengakses Tokomoe REST API</p>
        </div>
        <Button onClick={() => setShowCreate(true)}><Plus className="h-4 w-4 mr-2" />Generate New Key</Button>
      </div>

      {/* Info Card */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="pt-4">
          <div className="flex gap-3">
            <Key className="h-5 w-5 text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium">Cara menggunakan API Key</p>
              <p className="mt-1">Tambahkan header <code className="bg-blue-100 px-1 rounded">X-API-Key: sk_live_xxx...</code> pada setiap request ke endpoint <code className="bg-blue-100 px-1 rounded">/api/v1/*</code></p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Keys List */}
      {keys.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Key className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">Belum ada API Key</h3>
            <p className="text-muted-foreground mb-4">Generate API key pertama Anda untuk mulai menggunakan REST API</p>
            <Button onClick={() => setShowCreate(true)}><Plus className="h-4 w-4 mr-2" />Generate Key</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {keys.map(k => (
            <Card key={k.id}>
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center">
                      <Key className="h-5 w-5 text-slate-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{k.name}</span>
                        <Badge variant={k.is_active ? 'default' : 'destructive'}>
                          {k.is_active ? 'Active' : 'Revoked'}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                        <code className="bg-slate-100 px-2 py-0.5 rounded text-xs">{k.key_preview}</code>
                        <span>Created: {new Date(k.created_at).toLocaleDateString('id-ID')}</span>
                        {k.last_used_at && <span>Last used: {new Date(k.last_used_at).toLocaleDateString('id-ID')}</span>}
                        <span>Usage: {k.usage_count}x</span>
                      </div>
                    </div>
                  </div>
                  {k.is_active && (
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => setConfirmRegen(k)}>
                        <RefreshCw className="h-3.5 w-3.5 mr-1" />Regenerate
                      </Button>
                      <Button variant="destructive" size="sm" onClick={() => setConfirmRevoke(k)}>
                        <Trash2 className="h-3.5 w-3.5 mr-1" />Revoke
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Key Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader><DialogTitle>Generate API Key Baru</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nama Key</Label>
              <Input placeholder="e.g. Production, Staging, Mobile App" value={name} onChange={e => setName(e.target.value)} />
            </div>
            <Button className="w-full" onClick={handleCreate} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Generate Key
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Show New Key Dialog */}
      <Dialog open={showNewKey} onOpenChange={setShowNewKey}>
        <DialogContent>
          <DialogHeader><DialogTitle>API Key Anda</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
              <p className="text-sm text-amber-800">Simpan key ini sekarang. Key tidak akan ditampilkan lagi setelah dialog ini ditutup.</p>
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 p-3 bg-slate-900 text-green-400 rounded-lg text-sm break-all font-mono">{newKeyValue}</code>
              <Button variant="outline" size="icon" onClick={() => copyKey(newKeyValue)}>
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm Revoke Dialog */}
      <Dialog open={!!confirmRevoke} onOpenChange={() => setConfirmRevoke(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Revoke API Key?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Key <strong>{confirmRevoke?.name}</strong> akan dinonaktifkan. Semua request menggunakan key ini akan gagal.</p>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setConfirmRevoke(null)}>Batal</Button>
            <Button variant="destructive" onClick={() => handleRevoke(confirmRevoke.id)}>Revoke</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm Regenerate Dialog */}
      <Dialog open={!!confirmRegen} onOpenChange={() => setConfirmRegen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Regenerate API Key?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Key lama untuk <strong>{confirmRegen?.name}</strong> akan dihapus dan diganti key baru. Semua request menggunakan key lama akan gagal.</p>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setConfirmRegen(null)}>Batal</Button>
            <Button onClick={() => handleRegenerate(confirmRegen.id)}>Regenerate</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
